import type {
  CreateExpenseInput,
  LeftoverMode,
  PersonDto,
  SplitMode,
} from "@caloteiros/shared";
import { formatCents, splitExpense, SplitError } from "@caloteiros/shared";
import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell";
import { CurrencyInput } from "../components/CurrencyInput";
import { IconChevron, IconPlus, IconTrash } from "../components/Icons";
import { PersonChips, PersonSelect } from "../components/PersonPicker";
import { Avatar, ConfirmDialog, Field, LoadingScreen, Segmented, Spinner } from "../components/ui";
import { ApiError } from "../lib/api";
import { firstName, fromDateInputValue, toDateInputValue } from "../lib/format";
import {
  useCreateExpense,
  useDeleteExpense,
  useExpense,
  useGroup,
  useUpdateExpense,
} from "../lib/queries";
import { useToast } from "../lib/toast";

interface ItemDraft {
  key: string;
  name: string;
  amountCents: number;
  personIds: Set<string>;
}

const SPLIT_OPTIONS: { value: SplitMode; label: string }[] = [
  { value: "EQUAL", label: "Igual" },
  { value: "WEIGHT", label: "Peso" },
  { value: "EXACT", label: "Exato" },
  { value: "ITEMS", label: "Itens" },
];

export function ExpenseFormPage(): ReactElement {
  const { groupId = "", expenseId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isEditing = Boolean(expenseId);

  const group = useGroup(groupId);
  const existing = useExpense(groupId, expenseId);
  const createExpense = useCreateExpense(groupId);
  const updateExpense = useUpdateExpense(groupId, expenseId ?? "");
  const deleteExpense = useDeleteExpense(groupId);

  const members: PersonDto[] = useMemo(
    () => (group.data?.members ?? []).map((member) => member.person),
    [group.data],
  );

  const [amountCents, setAmountCents] = useState(0);
  const [description, setDescription] = useState("");
  const [dateValue, setDateValue] = useState(() => toDateInputValue(new Date().toISOString()));
  const [notes, setNotes] = useState("");
  const [splitMode, setSplitMode] = useState<SplitMode>("EQUAL");
  const [leftoverMode, setLeftoverMode] = useState<LeftoverMode>("PROPORTIONAL");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [singlePayerId, setSinglePayerId] = useState<string | null>(null);
  const [multiPayerMode, setMultiPayerMode] = useState(false);
  const [payerAmounts, setPayerAmounts] = useState<Record<string, number>>({});
  const [weights, setWeights] = useState<Record<string, number>>({});
  const [exactAmounts, setExactAmounts] = useState<Record<string, number>>({});
  const [coveredBy, setCoveredBy] = useState<Record<string, string | null>>({});
  const [items, setItems] = useState<ItemDraft[]>([]);
  const [advanced, setAdvanced] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Nova despesa: todo mundo entra na divisao e quem paga sou eu. E o caso
  // comum, e o caso comum tem que sair sem toque nenhum extra.
  useEffect(() => {
    if (isEditing || members.length === 0 || selected.size > 0) return;
    setSelected(new Set(members.map((person) => person.id)));
    setSinglePayerId(group.data?.myPersonId ?? members[0]?.id ?? null);
  }, [isEditing, members, selected.size, group.data]);

  // Edicao: recompoe o formulario a partir da despesa gravada.
  useEffect(() => {
    const expense = existing.data;
    if (!expense || !isEditing) return;

    setAmountCents(expense.amountCents);
    setDescription(expense.description);
    setDateValue(toDateInputValue(expense.occurredAt));
    setNotes(expense.notes ?? "");
    setSplitMode(expense.splitMode);
    setLeftoverMode(expense.leftoverMode);
    setSelected(new Set(expense.shares.map((share) => share.personId)));
    setWeights(Object.fromEntries(expense.shares.map((share) => [share.personId, share.weight])));
    setExactAmounts(
      Object.fromEntries(
        expense.shares.map((share) => [share.personId, share.exactAmountCents ?? 0]),
      ),
    );
    setCoveredBy(
      Object.fromEntries(
        expense.shares.map((share) => [share.personId, share.coveredByPersonId]),
      ),
    );
    setItems(
      expense.items.map((item, index) => ({
        key: `${item.id}-${index}`,
        name: item.name,
        amountCents: item.amountCents,
        personIds: new Set(item.shares.map((share) => share.personId)),
      })),
    );

    if (expense.payers.length > 1) {
      setMultiPayerMode(true);
      setPayerAmounts(
        Object.fromEntries(expense.payers.map((payer) => [payer.personId, payer.amountCents])),
      );
    } else {
      setSinglePayerId(expense.payers[0]?.personId ?? null);
    }

    // Quem editar algo fora do basico ja abre o painel avancado aberto.
    if (expense.splitMode !== "EQUAL" || expense.payers.length > 1) setAdvanced(true);
  }, [existing.data, isEditing]);

  const selectedPeople = members.filter((person) => selected.has(person.id));

  const payers = useMemo(() => {
    if (multiPayerMode) {
      return Object.entries(payerAmounts)
        .filter(([, value]) => value > 0)
        .map(([personId, value]) => ({ personId, amountCents: value }));
    }
    return singlePayerId ? [{ personId: singlePayerId, amountCents }] : [];
  }, [multiPayerMode, payerAmounts, singlePayerId, amountCents]);

  const payersTotal = payers.reduce((sum, payer) => sum + payer.amountCents, 0);

  // O preview roda a mesma funcao que o servidor vai rodar ao gravar.
  const preview = useMemo(() => {
    if (amountCents <= 0 || selectedPeople.length === 0) {
      return { shares: [], error: null as string | null };
    }
    try {
      const shares = splitExpense({
        amountCents,
        splitMode,
        leftoverMode,
        shares: selectedPeople.map((person) => ({
          personId: person.id,
          weight: weights[person.id] ?? 1,
          exactAmountCents: splitMode === "EXACT" ? (exactAmounts[person.id] ?? 0) : null,
          coveredByPersonId: coveredBy[person.id] ?? null,
        })),
        items:
          splitMode === "ITEMS"
            ? items.map((item) => ({
                name: item.name,
                amountCents: item.amountCents,
                shares: [...item.personIds].map((personId) => ({ personId })),
              }))
            : undefined,
      });
      return { shares, error: null };
    } catch (error) {
      return {
        shares: [],
        error: error instanceof SplitError ? error.message : "Não consegui dividir assim.",
      };
    }
  }, [amountCents, splitMode, leftoverMode, selectedPeople, weights, exactAmounts, coveredBy, items]);

  const nameOf = (personId: string): string =>
    members.find((person) => person.id === personId)?.displayName ?? "Alguém";

  const toggleSelected = (personId: string): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(personId)) next.delete(personId);
      else next.add(personId);
      return next;
    });
  };

  const submit = async (): Promise<void> => {
    if (amountCents <= 0) {
      toast.error("Informe o valor da despesa.");
      return;
    }
    if (!description.trim()) {
      toast.error("Escreva uma descrição.");
      return;
    }
    if (payersTotal !== amountCents) {
      toast.error("A soma do que cada um pagou precisa bater com o valor.");
      return;
    }
    if (preview.error) {
      toast.error(preview.error);
      return;
    }

    const payload: CreateExpenseInput = {
      description: description.trim(),
      notes: notes.trim() === "" ? undefined : notes.trim(),
      amountCents,
      occurredAt: fromDateInputValue(dateValue),
      splitMode,
      leftoverMode,
      payers,
      shares: selectedPeople.map((person) => ({
        personId: person.id,
        weight: splitMode === "WEIGHT" ? (weights[person.id] ?? 1) : 1,
        exactAmountCents: splitMode === "EXACT" ? (exactAmounts[person.id] ?? 0) : null,
        coveredByPersonId: coveredBy[person.id] ?? null,
      })),
      items:
        splitMode === "ITEMS"
          ? items.map((item) => ({
              name: item.name.trim() === "" ? "Item" : item.name.trim(),
              amountCents: item.amountCents,
              shares: [...item.personIds].map((personId) => ({ personId })),
            }))
          : undefined,
    };

    try {
      if (isEditing) await updateExpense.mutateAsync(payload);
      else await createExpense.mutateAsync(payload);
      toast.success(isEditing ? "Despesa atualizada." : "Despesa lançada.");
      navigate(`/app/grupos/${groupId}`, { replace: true });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Não consegui salvar.");
    }
  };

  if (group.isLoading || (isEditing && existing.isLoading)) return <LoadingScreen />;

  const saving = createExpense.isPending || updateExpense.isPending;

  return (
    <>
      <PageHeader
        title={isEditing ? "Editar despesa" : "Nova despesa"}
        back={`/app/grupos/${groupId}`}
        actions={
          isEditing ? (
            <button
              type="button"
              className="btn-icon"
              aria-label="Apagar despesa"
              onClick={() => setConfirmDelete(true)}
            >
              <IconTrash />
            </button>
          ) : undefined
        }
      />

      <main className="app-content stack-lg">
        <CurrencyInput valueCents={amountCents} onChange={setAmountCents} autoFocus={!isEditing} />

        <Field label="Descrição">
          <input
            className="input"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Carne, bebida, uber..."
            maxLength={120}
          />
        </Field>

        <Field as="div" label="Quem pagou">
          {multiPayerMode ? (
            <div className="stack">
              {members.map((person) => (
                <div key={person.id} className="person-row">
                  <Avatar name={person.displayName} color={person.avatarColor} size="sm" />
                  <span className="truncate">{person.displayName}</span>
                  <input
                    className="input numeric"
                    inputMode="numeric"
                    value={formatCents(payerAmounts[person.id] ?? 0)}
                    onChange={(event) => {
                      const digits = event.target.value.replace(/\D/g, "").slice(0, 9);
                      setPayerAmounts((current) => ({
                        ...current,
                        [person.id]: digits === "" ? 0 : Number.parseInt(digits, 10),
                      }));
                    }}
                    aria-label={`Quanto ${person.displayName} pagou`}
                  />
                </div>
              ))}
              <p className={payersTotal === amountCents ? "hint" : "error-text"}>
                Somado: {formatCents(payersTotal)} de {formatCents(amountCents)}
              </p>
              <button
                type="button"
                className="btn btn-sm btn-subtle"
                onClick={() => setMultiPayerMode(false)}
              >
                Voltar para um pagante só
              </button>
            </div>
          ) : (
            <div className="stack">
              <PersonSelect people={members} value={singlePayerId} onChange={setSinglePayerId} />
              <button
                type="button"
                className="btn btn-sm btn-subtle"
                onClick={() => {
                  setMultiPayerMode(true);
                  setPayerAmounts(singlePayerId ? { [singlePayerId]: amountCents } : {});
                }}
              >
                Mais de uma pessoa pagou
              </button>
            </div>
          )}
        </Field>

        <Field as="div" label="Dividir entre">
          <PersonChips people={members} selected={selected} onToggle={toggleSelected} />
        </Field>

        {/* O preview da divisao: aparece assim que da para calcular. */}
        {preview.error ? (
          <p className="error-text">{preview.error}</p>
        ) : preview.shares.length > 0 ? (
          <div className="split-preview">
            {preview.shares.map((share) => (
              <div key={share.personId} className="split-preview-row">
                <span>
                  {firstName(nameOf(share.personId))}
                  {share.chargedToPersonId !== share.personId ? (
                    <span className="faint"> (paga {firstName(nameOf(share.chargedToPersonId))})</span>
                  ) : null}
                </span>
                <strong className="numeric">{formatCents(share.amountCents)}</strong>
              </div>
            ))}
          </div>
        ) : null}

        <button
          type="button"
          className="advanced-toggle"
          onClick={() => setAdvanced((current) => !current)}
          aria-expanded={advanced}
        >
          Divisão avançada, data e observações
          <IconChevron
            style={{ transform: advanced ? "rotate(90deg)" : "none", transition: "transform 160ms" }}
          />
        </button>

        {advanced ? (
          <div className="stack-lg">
            <Field as="div" label="Como dividir">
              <Segmented
                label="Modo de divisão"
                value={splitMode}
                onChange={setSplitMode}
                options={SPLIT_OPTIONS}
              />
            </Field>

            {splitMode === "WEIGHT" ? (
              <Field as="div" label="Peso de cada um" hint="Levou acompanhante? Coloque 2.">
                <div className="stack">
                  {selectedPeople.map((person) => (
                    <div key={person.id} className="person-row">
                      <Avatar name={person.displayName} color={person.avatarColor} size="sm" />
                      <span className="truncate">{person.displayName}</span>
                      <input
                        className="input numeric"
                        type="number"
                        min={0}
                        step={0.5}
                        value={weights[person.id] ?? 1}
                        onChange={(event) =>
                          setWeights((current) => ({
                            ...current,
                            [person.id]: Number(event.target.value),
                          }))
                        }
                        aria-label={`Peso de ${person.displayName}`}
                      />
                    </div>
                  ))}
                </div>
              </Field>
            ) : null}

            {splitMode === "EXACT" ? (
              <Field as="div" label="Valor de cada um" hint="A soma precisa fechar com o total.">
                <div className="stack">
                  {selectedPeople.map((person) => (
                    <div key={person.id} className="person-row">
                      <Avatar name={person.displayName} color={person.avatarColor} size="sm" />
                      <span className="truncate">{person.displayName}</span>
                      <input
                        className="input numeric"
                        inputMode="numeric"
                        value={formatCents(exactAmounts[person.id] ?? 0)}
                        onChange={(event) => {
                          const digits = event.target.value.replace(/\D/g, "").slice(0, 9);
                          setExactAmounts((current) => ({
                            ...current,
                            [person.id]: digits === "" ? 0 : Number.parseInt(digits, 10),
                          }));
                        }}
                        aria-label={`Valor de ${person.displayName}`}
                      />
                    </div>
                  ))}
                </div>
              </Field>
            ) : null}

            {splitMode === "ITEMS" ? (
              <ItemsEditor
                items={items}
                setItems={setItems}
                people={selectedPeople}
                amountCents={amountCents}
                leftoverMode={leftoverMode}
                setLeftoverMode={setLeftoverMode}
              />
            ) : null}

            <Field
              as="div"
              label="Alguém está bancando a parte de outro?"
              hint="A dívida vai para quem bancou, mas a despesa continua registrada no nome de quem consumiu."
            >
              <div className="stack">
                {selectedPeople.map((person) => (
                  <div key={person.id} className="person-row">
                    <Avatar name={person.displayName} color={person.avatarColor} size="sm" />
                    <span className="truncate">{person.displayName}</span>
                    <PersonSelect
                      people={selectedPeople.filter((other) => other.id !== person.id)}
                      value={coveredBy[person.id] ?? null}
                      onChange={(value) =>
                        setCoveredBy((current) => ({ ...current, [person.id]: value }))
                      }
                      allowEmpty
                      emptyLabel="Paga a própria parte"
                    />
                  </div>
                ))}
              </div>
            </Field>

            <Field label="Data">
              <input
                className="input"
                type="date"
                value={dateValue}
                onChange={(event) => setDateValue(event.target.value)}
              />
            </Field>

            <Field label="Observações">
              <textarea
                className="textarea"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Onde foi, o que rolou..."
                maxLength={500}
              />
            </Field>
          </div>
        ) : null}

        <button
          type="button"
          className="btn btn-primary btn-block"
          onClick={() => void submit()}
          disabled={saving}
        >
          {saving ? <Spinner /> : isEditing ? "Salvar alterações" : "Lançar despesa"}
        </button>
      </main>

      <ConfirmDialog
        open={confirmDelete}
        title="Apagar despesa?"
        message="Ela sai das contas do rolê na hora."
        confirmLabel="Apagar"
        destructive
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          if (!expenseId) return;
          deleteExpense.mutate(expenseId, {
            onSuccess: () => {
              toast.success("Despesa apagada.");
              navigate(`/app/grupos/${groupId}`, { replace: true });
            },
            onError: () => toast.error("Não consegui apagar."),
          });
        }}
      />
    </>
  );
}

/** Editor de itens: cada linha da comanda com quem consumiu. */
function ItemsEditor({
  items,
  setItems,
  people,
  amountCents,
  leftoverMode,
  setLeftoverMode,
}: {
  items: ItemDraft[];
  setItems: (updater: (current: ItemDraft[]) => ItemDraft[]) => void;
  people: PersonDto[];
  amountCents: number;
  leftoverMode: LeftoverMode;
  setLeftoverMode: (mode: LeftoverMode) => void;
}): ReactElement {
  const itemsTotal = items.reduce((sum, item) => sum + item.amountCents, 0);
  const leftover = amountCents - itemsTotal;

  const update = (key: string, patch: Partial<ItemDraft>): void => {
    setItems((current) =>
      current.map((item) => (item.key === key ? { ...item, ...patch } : item)),
    );
  };

  return (
    <div className="stack">
      <span className="label">Itens da conta</span>

      {items.map((item) => (
        <div key={item.key} className="item-card">
          <div className="row">
            <input
              className="input grow"
              value={item.name}
              onChange={(event) => update(item.key, { name: event.target.value })}
              placeholder="Ex.: rodízio"
              maxLength={80}
            />
            <input
              className="input numeric"
              inputMode="numeric"
              style={{ maxWidth: 128, textAlign: "right" }}
              value={formatCents(item.amountCents)}
              onChange={(event) => {
                const digits = event.target.value.replace(/\D/g, "").slice(0, 9);
                update(item.key, { amountCents: digits === "" ? 0 : Number.parseInt(digits, 10) });
              }}
              aria-label={`Valor de ${item.name || "item"}`}
            />
            <button
              type="button"
              className="btn-icon"
              aria-label="Remover item"
              onClick={() => setItems((current) => current.filter((row) => row.key !== item.key))}
            >
              <IconTrash width={18} height={18} />
            </button>
          </div>

          <PersonChips
            people={people}
            selected={item.personIds}
            onToggle={(personId) => {
              const next = new Set(item.personIds);
              if (next.has(personId)) next.delete(personId);
              else next.add(personId);
              update(item.key, { personIds: next });
            }}
          />
        </div>
      ))}

      <button
        type="button"
        className="btn btn-subtle btn-block"
        onClick={() =>
          setItems((current) => [
            ...current,
            {
              key: `item-${Date.now()}-${current.length}`,
              name: "",
              amountCents: 0,
              personIds: new Set(people.map((person) => person.id)),
            },
          ])
        }
      >
        <IconPlus width={18} height={18} />
        Adicionar item
      </button>

      <div className="split-preview">
        <div className="split-preview-row">
          <span>Itens somam</span>
          <strong className="numeric">{formatCents(itemsTotal)}</strong>
        </div>
        <div className="split-preview-row">
          <span>Sobra (taxa, couvert, entrega)</span>
          <strong className={`numeric ${leftover < 0 ? "negative" : ""}`}>
            {formatCents(leftover)}
          </strong>
        </div>
      </div>

      {leftover > 0 ? (
        <Field as="div" label="Como dividir a sobra">
          <Segmented
            label="Divisão da sobra"
            value={leftoverMode}
            onChange={setLeftoverMode}
            options={[
              { value: "PROPORTIONAL", label: "Quem consumiu mais" },
              { value: "EQUAL", label: "Igual para todos" },
            ]}
          />
        </Field>
      ) : null}
    </div>
  );
}
